/**
 * What a person chooses for one resume before it is tailored: the
 * keywords they do not want led with, and the instructions for this resume.
 * Pure: every function here returns a new value. The sample server, which
 * the end to end suites load outside the app, reads this, so it imports
 * nothing of the app's.
 */

/** The most characters the instructions for one resume take. */
export const MAX_TAILOR_INSTRUCTIONS = 500;

/** The most keywords one tailor may turn off, and the longest one. */
export const MAX_KEYWORDS_OFF = 15;
export const MAX_KEYWORD_OFF_CHARS = 80;

export interface TailorChoices {
    /** The keywords not to lead with, in the posting's order. Empty, every one is on. */
    keywordsOff: string[];
    /** Set only when it differs from the saved ones. Empty, none for this resume. */
    instructions?: string;
}

/** A posting's keywords as choices: its order, each word once. */
export function tailorKeywords(terms: readonly string[]): string[] {
    return [...new Set(terms)];
}

/** `off` with `term` turned the other way. */
export function toggledKeyword(off: readonly string[], term: string): string[] {
    return off.includes(term) ? off.filter((entry) => entry !== term) : [...off, term];
}

/** A skill in lower case with each run of white space one space, as the resume service compares them. */
function folded(skill: string): string {
    return skill.split(/\s+/).filter(Boolean).join(" ").toLowerCase();
}

/**
 * A resume's skills with the ones a person says they have added after them:
 * trimmed, none blank, and none the resume already says in any case or
 * spacing. The skills given are left as they were.
 */
export function withSkills(skills: readonly string[], added: readonly string[]): string[] {
    const seen = new Set(skills.map(folded));
    const next = [...skills];
    for (const skill of added.map((entry) => entry.trim())) {
        if (!skill || seen.has(folded(skill))) continue;
        seen.add(folded(skill));
        next.push(skill);
    }
    return next;
}

/**
 * What envoy refuses because a person would not see it and a model would:
 * control characters, directional overrides, zero width spaces and Unicode
 * tag characters. A zero width joiner stays: emoji need it.
 */
const UNREADABLE =
    /[\p{Cc}\u200B\u2060\uFEFF\uFFFE\uFFFF\u202A-\u202E\u2066-\u2069\u{E0000}-\u{E007F}]/u;

/** Whether `text` holds what envoy refuses. A line break and a tab are text in instructions. */
export function hasUnreadable(text: string): boolean {
    return UNREADABLE.test(text.replace(/[\n\r\t]/g, ""));
}

/** Why these instructions cannot go, as the box says it. */
export function instructionsFault(text: string): string | undefined {
    // Trimmed code points, as envoy counts them.
    if (Array.from(text.trim()).length > MAX_TAILOR_INSTRUCTIONS) {
        return `Instructions are ${MAX_TAILOR_INSTRUCTIONS} characters at most.`;
    }
    return hasUnreadable(text) ? "Instructions hold characters that cannot be read." : undefined;
}

/** The choices to hand over: only what differs from what would happen anyway. */
export function tailorChoices(
    terms: readonly string[],
    off: readonly string[],
    typed: string,
    saved: string | null,
): TailorChoices {
    const keywordsOff = terms.filter((term) => off.includes(term));
    const instructions = typed.trim();
    return instructions === (saved ?? "").trim() ? { keywordsOff } : { keywordsOff, instructions };
}
